/**
 * Shared HTTP transport for the axios facades.
 *
 * ONE place that defines the transport concerns consumed by both remaining
 * axios facades (src/services/api.js and src/api/agentClient.ts):
 *   - axios instance construction (base URL comes from src/config/api.ts —
 *     the only module allowed to know backend origins)
 *   - Authorization header injection from the shared token accessor
 *     (src/services/authTokenStore.ts)
 *   - X-Tenant-ID header injection (opt-in, api.js semantics)
 *   - the canonical 401 → refresh → retry-once response interceptor
 *     (single-flight refresh is delegated to refreshAuthToken(), which
 *     dedupes concurrent 401s via an in-flight Promise)
 *   - shared error normalization matching agentClient.handleError output
 *
 * The two facades keep their public surfaces; only their transport guts
 * live here (strangler pattern).
 */

import axios from 'axios';
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

import { getAuthToken, refreshAuthToken } from '../services/authTokenStore';
import * as tokenStore from '../utils/tokenStore';

/** Error payload shape returned by the Go backend. */
export interface NormalizedApiErrorBody {
  error: string;
  details?: string;
}

export type PrivateTelemetryRequestConfig = AxiosRequestConfig & {
  privateTelemetry?: boolean;
};

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  privateTelemetry?: boolean;
  _retry?: boolean;
  metadata?: { requestId: string; startTime: number };
};

export interface CreateHttpClientOptions {
  /** Base URL for the instance. Resolve it via src/config/api.ts. */
  baseURL: string;
  /** Client name used in request ids and log prefixes. */
  name?: string;
  /** Request timeout in ms (default 30000). */
  timeout?: number;
  /** Extra default headers merged over `Content-Type: application/json`. */
  headers?: Record<string, string>;
  /**
   * Attach the auth request interceptor (default true). Set false for
   * clients that must send unauthenticated requests (e.g. the refresh
   * endpoint client).
   */
  auth?: boolean;
  /**
   * When true (agentClient semantics) the Authorization header is always
   * (re)set from the token store. When false (api.js semantics) an
   * Authorization header already present on the request is preserved.
   */
  overwriteAuthHeader?: boolean;
  /**
   * Attach X-Tenant-ID from localStorage('tenant_id') when present
   * (api.js semantics). Default false.
   */
  tenantHeader?: boolean;
  /**
   * Attach per-request metadata (requestId, startTime) and debug logging
   * (api.js semantics). Default false.
   */
  requestMetadata?: boolean;
}

export interface AuthRefreshInterceptorOptions {
  /** Log prefix, e.g. 'main-api'. */
  name?: string;
  /**
   * api.js semantics: response/error duration logging plus 5xx reporting
   * through the provided errorLogger. Default false.
   */
  logging?: boolean;
  /** Error logging sink used for >=500 responses when `logging` is on. */
  errorLogger?: {
    logError: (payload: {
      error: { name: string; message: string; stack?: string };
      context: Record<string, unknown>;
    }) => void;
  } | null;
  /**
   * What to do when the 401 cannot be recovered by a refresh:
   *  - 'logout-redirect' (api.js semantics): wipe stored tokens, redirect
   *    to /login, reject with a user-facing message.
   *  - 'reject' (agentClient semantics): reject with the ORIGINAL axios
   *    error, no redirect — deliberately, to avoid redirect loops with the
   *    api.js clients (see agentClient git history).
   */
  onAuthFailure?: 'logout-redirect' | 'reject';
  /**
   * api.js semantics: map 403 / >=500 / network errors to user-facing
   * messages. Default false (agentClient formats via normalizeApiError).
   */
  normalizeMessages?: boolean;
}

const DEFAULT_TIMEOUT_MS = 30000;

function privateTelemetryContext(
  config?: RetriableRequestConfig,
  status?: number
) {
  return {
    status,
    url: config?.url?.split(/[?#]/)[0],
    method: config?.method?.toUpperCase(),
    requestId: config?.metadata?.requestId,
    duration: config?.metadata?.startTime
      ? `${Date.now() - config.metadata.startTime}ms`
      : 'unknown',
  };
}

/**
 * Create an axios instance carrying the canonical request interceptor
 * (Authorization from the shared token accessor, optional X-Tenant-ID,
 * optional request metadata/debug logging).
 */
export function createHttpClient(
  options: CreateHttpClientOptions
): AxiosInstance {
  const {
    baseURL,
    name = 'api',
    timeout = DEFAULT_TIMEOUT_MS,
    headers,
    auth = true,
    overwriteAuthHeader = false,
    tenantHeader = false,
    requestMetadata = false,
  } = options;

  const client = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });

  if (auth) {
    client.interceptors.request.use(
      config => {
        const token = getAuthToken();
        if (token && (overwriteAuthHeader || !config.headers.Authorization)) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        if (tenantHeader) {
          const tenantId = localStorage.getItem('tenant_id');
          if (tenantId) {
            config.headers['X-Tenant-ID'] = tenantId;
          }
        }

        if (requestMetadata) {
          (config as RetriableRequestConfig).metadata = {
            requestId: `${name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            startTime: Date.now(),
          };
        }

        return config;
      },
      error => {
        console.error(
          `[${name}] Request interceptor error:`,
          error.config?.privateTelemetry
            ? privateTelemetryContext(error.config, error.response?.status)
            : error
        );
        return Promise.reject(error);
      }
    );
  }

  return client;
}

/**
 * Attach the canonical 401-refresh response interceptor to a client.
 *
 * Behavior (lifted from services/api.js's proven logic):
 *  - single-flight refresh: refreshAuthToken() dedupes concurrent 401s so
 *    N parallel failures share ONE refresh call
 *  - `_retry` flag prevents infinite loops when the refreshed token is
 *    also rejected
 *  - stale/invalid-format token codes short-circuit the refresh attempt
 *  - on unrecoverable 401, either logout+redirect (api.js) or reject the
 *    original error (agentClient) per `onAuthFailure`
 */
export function attachAuthRefreshInterceptor(
  client: AxiosInstance,
  options: AuthRefreshInterceptorOptions = {}
): void {
  const {
    name = 'api',
    logging = false,
    errorLogger = null,
    onAuthFailure = 'reject',
    normalizeMessages = false,
  } = options;

  client.interceptors.response.use(
    response => {
      return response;
    },
    async (error: AxiosError<NormalizedApiErrorBody>) => {
      const originalRequest = error.config as
        RetriableRequestConfig | undefined;
      const privateTelemetry = originalRequest?.privateTelemetry === true;
      const rejectWithMessage = (message: string) =>
        Promise.reject(
          privateTelemetry
            ? { status: error.response?.status, message }
            : { ...error, message }
        );

      if (logging) {
        const duration = originalRequest?.metadata?.startTime
          ? Date.now() - originalRequest.metadata.startTime
          : 'unknown';

        console.error(
          `[${name}] API Error:`,
          privateTelemetry
            ? privateTelemetryContext(originalRequest, error.response?.status)
            : {
                status: error.response?.status,
                url: originalRequest?.url,
                duration: `${duration}ms`,
                message: error.message,
                requestId: originalRequest?.metadata?.requestId,
              }
        );

        // The persistent logger enriches records with page/breadcrumb data; private requests skip it entirely.
        if (
          !privateTelemetry &&
          errorLogger &&
          (error.response?.status ?? 0) >= 500
        ) {
          errorLogger.logError({
            error: {
              name: 'APIError',
              message: `API ${error.response?.status}: ${error.message}`,
              stack: error.stack,
            },
            context: {
              type: 'api_error',
              clientName: name,
              url: originalRequest?.url,
              method: originalRequest?.method?.toUpperCase(),
              duration: `${duration}ms`,
              requestId: originalRequest?.metadata?.requestId,
              status: error.response?.status,
              statusText: error.response?.statusText,
              responseData: error.response?.data,
              requestData: originalRequest?.data,
              headers: originalRequest?.headers,
            },
          });
        }
      }

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        const code = (error.response?.data as { code?: string } | undefined)
          ?.code;
        const staleOpaque =
          code === 'auth_token_invalid_format' ||
          code === 'auth_token_invalid_signature';

        let freshToken: string | null = null;
        if (!staleOpaque) {
          try {
            freshToken = await refreshAuthToken();
          } catch {
            freshToken = null;
          }
        }
        // When no refresher is registered, refreshAuthToken() falls back to
        // the current token — replaying the request with the very token the
        // server just rejected guarantees a second identical 401. Only
        // retry when the refresh actually produced a different token.
        if (freshToken === getAuthToken() && originalRequest.headers) {
          const sent = String(
            originalRequest.headers.Authorization || ''
          ).replace(/^Bearer\s+/i, '');
          if (sent && sent === freshToken) {
            freshToken = null;
          }
        }
        if (freshToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${freshToken}`;
          return client(originalRequest);
        }

        if (onAuthFailure === 'reject') {
          // agentClient semantics: no redirect, surface the original error.
          return privateTelemetry
            ? rejectWithMessage('Session expired. Please sign in again.')
            : Promise.reject(error);
        }

        try {
          tokenStore.removeAccessToken();
          tokenStore.removeRefreshToken();
        } catch {
          /* storage unavailable — nothing to wipe */
        }
        if (privateTelemetry) {
          console.warn(`[${name}] Private request authentication failed.`);
        } else if (staleOpaque) {
          console.warn(
            `[${name}] Stale opaque/invalid token detected (code=%s). ` +
              'Wiping local token and redirecting to /login. ' +
              'If this loops, clear site data in DevTools → Application → Storage.',
            code
          );
        } else {
          console.warn(
            `[${name}] Session expired (401, code=%s). Refreshing via Logto SDK failed; redirecting to /login.`,
            code || 'auth_required'
          );
        }
        const here = typeof window !== 'undefined' ? window.location : null;
        if (here && here.pathname !== '/login') {
          here.replace(
            here.pathname === '/'
              ? '/login'
              : `/login?next=${encodeURIComponent(here.pathname)}`
          );
        }
        return rejectWithMessage(
          staleOpaque
            ? 'Your session is no longer valid. Please sign in again.'
            : 'Session expired. Please sign in again.'
        );
      }

      if (normalizeMessages) {
        if (error.response?.status === 403) {
          return rejectWithMessage(
            'Access denied. You do not have permission to perform this action.'
          );
        }

        if ((error.response?.status ?? 0) >= 500) {
          return rejectWithMessage('Server error. Please try again later.');
        }

        if (error.code === 'ECONNABORTED' || error.code === 'ENOTFOUND') {
          return rejectWithMessage(
            'Network error. Please check your connection and try again.'
          );
        }
      }

      return privateTelemetry
        ? rejectWithMessage('Private API request failed.')
        : Promise.reject(error);
    }
  );
}

/**
 * Shared error normalization. Output format matches
 * AgentApiClient.handleError exactly: console format included.
 */
export function normalizeApiError(error: unknown): Error {
  if (!axios.isAxiosError(error)) {
    console.error('API Error (Unknown):', error);
    return error as Error;
  }

  const { response, config } = error;
  const target = `${config?.method?.toUpperCase() || 'REQUEST'} ${config?.url || '(unknown url)'}`;

  // A request that never reached the server has no `response`. Logging
  // `response.status`/`response.data` unconditionally printed
  // "API Error: undefined undefined" and hid which call actually failed.
  if (response) {
    console.error(`API Error: ${response.status} ${target}`, response.data);
  } else {
    console.error(
      `API Error: ${error.code || 'network failure'} ${target} — ${error.message}`
    );
  }

  const apiError = response?.data as NormalizedApiErrorBody | undefined;
  const normalized = new Error(apiError?.error || error.message);
  // Carry the HTTP status on the Error so retry policies can branch on the
  // real status instead of sniffing message substrings (the messages here
  // are already rewritten and rarely contain the status code).
  (normalized as Error & { status?: number }).status = response?.status;
  return normalized;
}
